import React, { FormEvent, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { footerColumns, footerLegal, footerServices, footerSocial } from '../data/content';
import { Link, useLocation } from 'react-router-dom';
import { OPEN_COOKIE_SETTINGS } from './CookieBanner';
import { ConsentCheckbox } from './ConsentCheckbox';
import { CountrySelect } from './CountrySelect';
import { LanguageMenu } from './LanguageMenu';
import { useLanguage } from '../contexts/LanguageContext';
import type { UiKey } from '../data/i18n/en';
import { saveSubmission, SUBMISSION_ERROR } from '../utils/submissions';
type FooterLink = {
  label: string;
  href: string;
  key?: UiKey;
};
const headingClass = 'text-label font-bold uppercase text-paper/70';
const linkClass = 'text-small font-medium text-paper transition-opacity duration-150 hover:opacity-60';
const fieldClass = 'w-full border-0 bg-transparent px-0 py-2 text-[16px] text-paper placeholder:text-paper/70 focus:outline-none focus:ring-0';
type LinkGroupProps = {
  title: string;
  links: FooterLink[];
};
export function Footer() {
  const {
    pathname
  } = useLocation();
  const {
    t,
    locale,
    switchLocale
  } = useLanguage();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'error' | 'sending' | 'failed' | 'done'>('idle');
  const [city, setCity] = useState('');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state === 'sending') return;
    setConsentError(!consent);
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setState('error');
      return;
    }
    if (!consent) return;
    setState('sending');
    const ok = await saveSubmission({
      source: 'footer_newsletter',
      email,
      details: {
        consent: true
      }
    });
    setState(ok ? 'done' : 'failed');
  };
  const onLocate = (e: FormEvent) => {
    e.preventDefault();
    document.getElementById('destinations')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  // The Venturist experience ends on its own single-line footer
  if (pathname === '/venturist') return null;
  return <footer className="bg-ink text-paper" data-id="element-480" data-mp="yruz8de0jdkm">
      <div className="mx-auto w-full max-w-[1360px] px-6 pb-16 pt-20 md:px-6 md:pb-24 md:pt-28" data-id="element-481" data-mp="13t0xsructv22">
        <div className="grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-[auto_1fr] lg:gap-y-14" data-id="element-482" data-mp="18k97zq3idkga">
          {footerColumns.map(column => <LinkGroup key={column.title} title={t(column.titleKey)} links={column.links} data-id="element-483" data-mp="1hkfeut4pbm6h" />)}

          {/* Spans both rows on desktop so the link columns on the left stack without a gap */}
          <div className="md:col-span-2 lg:col-start-3 lg:row-span-2 lg:row-start-1" data-id="element-484" data-mp="1rildn1v7oc74">
            <h3 className={headingClass} data-id="element-485" data-mp="3ob549wa2ip1y">{t('footer.locator')}</h3>
            <form onSubmit={onLocate} className="mt-5" data-id="element-486" data-mp="3oczpzct5yl73">
              <label htmlFor="locator" className="sr-only" data-id="element-487" data-mp="3t9q805oj3qv5">
                {t('footer.locatorPlaceholder')}
              </label>
              <div className="flex items-center border-b border-paper/60 focus-within:border-paper" data-id="element-488" data-mp="2agomjdlikigf">
                <input id="locator" type="text" value={city} onChange={e => setCity(e.target.value)} placeholder={t('footer.locatorPlaceholder')} className={fieldClass} data-id="element-489" data-mp="25mu74mcxypn5" />
                <button type="submit" aria-label={t('footer.findAtelier')} className="transition-opacity duration-150 hover:opacity-60" data-id="element-490" data-mp="2tdg1jau0wbeo">
                  <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} data-id="element-491" data-mp="2kdb67z23t3gv" />
                </button>
              </div>
            </form>

            <h3 className={`mt-12 ${headingClass}`} data-id="element-492" data-mp="2fdf30218dg2d">{t('footer.lettersTitle')}</h3>
            <p className="mt-5 max-w-[620px] text-small text-paper" data-id="element-493" data-mp="18yecibuz5q36">{t('footer.lettersBody')}</p>
            {state === 'done' ? <p className="mt-6 border-b border-paper/60 py-2 text-[16px]" role="status" data-id="element-494" data-mp="13xqdb2wle2xv">
                {t('footer.lettersThanks')}
              </p> : <form onSubmit={onSubmit} noValidate className="mt-6" data-id="element-495" data-mp="2wnhllw1cjwc1">
                <label htmlFor="newsletter" className="sr-only" data-id="element-496" data-mp="2ivb86hpkw3ga">
                  {t('footer.email')}
                </label>
                <div className="flex items-center border-b border-paper/60 focus-within:border-paper" data-id="element-497" data-mp="2nv7c6gy9zuvu">
                  <input id="newsletter" type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                if (state === 'error') setState('idle');
              }} placeholder={t('footer.email')} className={fieldClass} aria-invalid={state === 'error'} data-id="element-498" data-mp="3bkrsst6yig1y" />
                  <button type="submit" aria-label={t('footer.signUp')} className="transition-opacity duration-150 hover:opacity-60" data-id="element-499" data-mp="1v570nz99rjk0">
                    <ChevronRightIcon className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} data-id="element-500" data-mp="1nkmp2amyul5y" />
                  </button>
                </div>
                {state === 'error' && <p className="mt-2 text-micro text-paper" role="alert" data-id="element-501" data-mp="1wmw1j61soolz">
                    {t('footer.emailError')}
                  </p>}
                {state === 'failed' && <p className="mt-2 text-micro text-paper" role="alert" data-id="element-502" data-mp="13x6v2d6bfwcz">
                    {SUBMISSION_ERROR}
                  </p>}
                <ConsentCheckbox id="newsletter-consent" tone="dark" checked={consent} onChange={v => {
              setConsent(v);
              if (v) setConsentError(false);
            }} error={consentError} className="mt-4 max-w-[620px]" data-id="element-503" data-mp="zuk0i4i9fxc3" />
              </form>}

            <h3 className={`mt-12 ${headingClass}`} data-id="element-504" data-mp="2gax3e44pikct">{t('lang.label')}</h3>
            <div className="mt-5" data-id="element-505" data-mp="2g8sxjgn8zb9w">
              <LanguageMenu value={locale} onChange={switchLocale} label={t('lang.label')} tone="dark" placement="up" variant="link" data-id="element-506" data-mp="2l8yikdz0hzj0" />
            </div>

            <h3 className={`mt-12 ${headingClass}`} data-id="element-507" data-mp="2v1cl38xwza68">{t('footer.country')}</h3>
            <div className="mt-5" data-id="element-508" data-mp="1xdm26dzilguq">
              <CountrySelect className={linkClass} data-id="element-509" data-mp="22am88owd6jlu" />
            </div>
          </div>

          <LinkGroup title={t(footerServices.titleKey)} links={footerServices.links} data-id="element-510" data-mp="22cp6fw7o7j1n" />
          <LinkGroup title={t(footerSocial.titleKey)} links={footerSocial.links} data-id="element-511" data-mp="27c1w0opxf47x" />
        </div>

        {/* House marks — never translated */}
        <div className="mt-16 flex items-center gap-8" dir="ltr" data-id="element-512" data-mp="3ipvuxswm3v0f">
          <span className="flex h-10 w-10 items-center justify-center bg-ash font-logo text-[15px] tracking-[0.06em] text-ink" data-id="element-513" data-mp="3irgszsi4xw31">
            SL
          </span>
          <span className="flex flex-col items-center leading-none text-paper/80" data-id="element-514" data-mp="1b22mnrnbmi3f">
            <span className="font-logo text-[7px] tracking-[0.4em]" data-id="element-515" data-mp="172trqjgjoe6a">SUPERLUXURY</span>
            <span className="mt-1 font-display text-[13px] tracking-[0.18em]" data-id="element-516" data-mp="12a8zhmdae94v">ATELIER</span>
          </span>
        </div>

        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-id="element-517" data-mp="xb66j0akztd9">
          <p className="text-small text-paper/50" data-id="element-518" data-mp="sdnrqt9vujxc">
            © {new Date().getFullYear()} KAYS VENTURES FZCO · Superluxury · superluxury.io – {t('footer.rights')}
          </p>
          {/* Legal links stay in English, like the documents they open */}
          <ul className="flex flex-wrap gap-x-6 gap-y-2" lang="en" data-id="element-519" data-mp="edd19rchj7cp">
            {footerLegal.map(link => <li key={link.label} data-id="element-520" data-mp="2dqqxhqwj8jtm">
                <FooterAnchor link={link} className="text-small text-paper/50 transition-opacity duration-150 hover:opacity-70" data-id="element-521" data-mp="2dpp8eiiolwsw" />
              </li>)}
          </ul>
        </div>

        {/* Full-width wordmark */}
        <svg dir="ltr" viewBox="0 0 1000 100" className="mt-28 block h-auto w-full md:mt-40" role="img" aria-label="Superluxury" preserveAspectRatio="none" data-id="element-522" data-mp="1hnv2fs95njpu">
          <text x="0" y="99" textLength="1000" lengthAdjust="spacingAndGlyphs" fill="currentColor" className="font-logo" style={{
          fontSize: 140
        }} data-id="element-523" data-mp="1cusa1ng46gb8">
            SUPERLUXURY
          </text>
        </svg>
      </div>
    </footer>;
}
function LinkGroup({
  title,
  links
}: LinkGroupProps) {
  return <nav aria-label={title} data-id="element-524" data-mp="tvxu4b4wz9th">
      <h3 className={headingClass} data-id="element-525" data-mp="tx8xwayd0hkl">{title}</h3>
      <ul className="mt-5 space-y-4" data-id="element-526" data-mp="ty0tvmvvchbe">
        {links.map(link => <li key={link.label} data-id="element-527" data-mp="2jbniqk69bkat">
            <FooterAnchor link={link} className={linkClass} data-id="element-528" data-mp="2j9b4agzpjs2i" />
          </li>)}
      </ul>
    </nav>;
}
function FooterAnchor({
  link,
  className
}: {
  link: FooterLink;
  className: string;
}) {
  const {
    pathname
  } = useLocation();
  const {
    t
  } = useLanguage();
  const label = link.key ? t(link.key) : link.label;
  if (link.href === '#cookie-settings') {
    return <button type="button" onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS))} className={className} data-id="element-529" data-mp="mdzo4glxygv0">
        {label}
      </button>;
  }
  if (link.href.startsWith('/')) {
    return <Link to={link.href} className={className} data-id="element-530" data-mp="vffvsgldg4vj">
        {label}
      </Link>;
  }
  if (link.href.startsWith('#') && pathname !== '/') {
    return <Link to={`/${link.href}`} className={className} data-id="element-531" data-mp="15aho1lay1n2c">
        {label}
      </Link>;
  }
  const external = link.href.startsWith('http');
  return <a href={link.href} className={className} {...external ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {}} data-id="element-532" data-mp="372w3mdq0mffl">
      {label}
    </a>;
}